import axios from 'axios'

import UI from '@/utils/ui'
import Router from '@/router'
import { ApiPre } from '@/config'

import { filterNull, toType } from '@/utils/tools'
import Cache from '@/utils/localstore'
import { getToken } from '@/utils/auth'

// 上传进度函数
const onUploadProgress = progress => {
  const complete = progress.loaded / progress.total * 100
  UI.loading.progress(complete)
}

// 创建 Axios 对象
const Axios = axios.create({
  baseURL: ApiPre, // url = base url + request url
  timeout: 60000 // request timeout
})

// 请求前处理
Axios.interceptors.request.use(
  config => {
    // JSON 类型数据先滤空
    config.data = filterNull(config.data)
    config.params = filterNull(config.params)

    const token = getToken()
    if (token) {
      config.headers.token = token
    }

    config.onUploadProgress = onUploadProgress
    UI.loading.open()
    // console.log(config)
    return config
  },
  error => {
    UI.loading.close()
    return Promise.reject(error)
  }
)

// 请求后处理
Axios.interceptors.response.use(
  response => {
    UI.loading.close()
    const res = response.data
    if (response.status !== 200 || res.status !== 0) {
      UI.toast.error(res.msg || res.data || 'Error')
      return Promise.reject(new Error(res.msg || res.data || 'Error'))
    } else {
      return Promise.resolve(res)
    }
  },
  error => {
    UI.loading.close()
    const { response } = error
    if (response && response.status === 404) {
      UI.toast.error('API undefined')
    } else if (response && response.status === 401) {
      UI.toast.error('您未登录或者登录超时，请重新登录')
      console.log(Router.push('/user/login'))
    } else {
      UI.toast.error((response.data && response.data.msg) || error.message || 'Error')
      return Promise.reject(error)
    }
  }
)

// 普通 get 方法
export const get = (url, params = {}) => {
  return Axios({ url, params, method: 'get' })
}

// 普通 post 方法
export const post = (url, data = {}) => {
  return Axios({ url, data, method: 'post' })
}

// 普通 put 方法
export const put = (url, data = {}) => {
  return Axios({ url, data, method: 'put' })
}

// 上传文件
export const upload = (url, data = {}) => {
  return post({
    url,
    data,
    method: 'post',
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/* 带有缓存数据的接口请求方法
   如缓存有数据，且没有时效，则直接返回缓存数据，否则请求接口获取
   url        String      接口地址
   method     String      'post' or 'get'
   data       Object      {} or null
   cacheName  String      缓存名称，不可重复
   cacheTime  Number      缓存时效，单位为小时
   processor  Function    数据处理函数 (参考用法 @/api/common.js)
*/
export const apiCache = async params => {
  const { url, method = 'post', data = null, cacheName, cacheTime, processor } = params
  if (!url || toType(url) !== 'string') {
    throw new Error('apiCache 方法 url 参数有误')
  }
  if (!cacheName || toType(cacheName) !== 'string') {
    throw new Error('apiCache 方法 cacheName 参数有误')
  }
  if (cacheTime && toType(cacheTime) !== 'number') {
    throw new Error('apiCache 方法 cacheTime 参数有误，只能为空或正整数，单位为小时')
  }
  if (processor && toType(processor) !== 'function') {
    throw new Error('apiCache 方法 processor 参数有误，只能为空或函数，为函数时，必须有返回值')
  }
  if (data && toType(data) !== 'object') {
    throw new Error('apiCache 方法 data 参数有误，只能为空或对象，含义是请求接口参数')
  }

  try {
    const cacheData = Cache.get(cacheName)
    if (cacheData) {
      return cacheData
    } else {
      try {
        const ajax = method === 'post' ? post : get
        const r = await ajax(url, data)
        const res = toType(r) === 'array' ? r : (r.data || {})
        const resData = processor ? processor(res) : res
        Cache.set(cacheName, resData, cacheTime)
        return resData
      } catch (e) {
        throw new Error(e)
      }
    }
  } catch (e) {
    // console.error(e)
    throw new Error(e)
  }
}

export default Axios
